import { useEffect } from "react";
import { GlobalStyle } from "./styles/GlobalStyle";
import {
  BrowserRouter as Router,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { store, RootState } from "./store";
import { FontSizeSetting } from "./utils/fontUtils";
import AppLayout from "./layout/AppLayout/AppLayout";
import AppRoutes from "./routes/AppRoutes";
import { isPublicRoute, isPrivateRoute } from "./routes/routesConfig";
import { isUserAuthenticated } from "./utils/authUtils";
import { initializeTokenRefresh, stopTokenRefresh } from "./utils/tokenRefresh";
import { ModalContainer } from "reoverlay";
import "reoverlay/lib/ModalWrapper.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GlassmorphismToastStyles } from "./components/GlassmorphismToast/GlassmorphismToastStyles";
import { captureBeforeInstallPrompt } from "./utils/a2hs/a2hsInstallPrompt";
import { syncA2hsInstalledFromDisplayMode } from "./utils/a2hs/maybeShowA2hsPrompt";

interface RouteGuardProps {
  children: JSX.Element;
}

const RouteGuard = ({ children }: RouteGuardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  useEffect(() => {
    const isAuthenticated = isUserAuthenticated();

    if (isAuthenticated) {
      initializeTokenRefresh();
    } else {
      stopTokenRefresh();
    }

    if (!isAuthenticated && isPrivateRoute(pathname)) {
      navigate("/login", { replace: true, state: { from: location } });
    } else if (
      isAuthenticated &&
      isPublicRoute(pathname) &&
      (pathname === "/login" ||
        pathname === "/" ||
        pathname === "/register" ||
        pathname === "/forgot-password")
    ) {
      navigate("/home", { replace: true });
    }
  }, [pathname, navigate]);

  useEffect(() => {
    const handleFocus = () => {
      initializeTokenRefresh();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        initializeTokenRefresh();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      stopTokenRefresh();
    };
  }, []);

  return <>{children}</>;
};

const AppWithGlobalStyle = () => {
  const fontSize = useSelector(
    (state: RootState) => state.userInfo.fontSize,
  ) as FontSizeSetting;

  useEffect(() => {
    syncA2hsInstalledFromDisplayMode();
    return captureBeforeInstallPrompt();
  }, []);

  return (
    <>
      <GlobalStyle fontSize={fontSize} />
      <GlassmorphismToastStyles />
      <ToastContainer
        toastClassName="glassmorphism-toast-wrapper"
        position="top-center"
        autoClose={3000}
        hideProgressBar
        closeButton={false}
      />
      <Router>
        <RouteGuard>
          <AppLayout>
            <AppRoutes />
          </AppLayout>
        </RouteGuard>
        <ModalContainer />
      </Router>
    </>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <AppWithGlobalStyle />
    </Provider>
  );
};

export default App;
